import type { StateCreator } from 'zustand';
import type { ItineraryState } from '../itinerary-store';

/**
 * Tracks the dirty/hydrated state of user-level preferences (layer
 * toggles, favourites, theme override, etc.). Mirrors the per-trip
 * dirty machinery from `sync-bridge.ts`, but the unit is the entire
 * user-prefs record — preferences are tiny so we ship the whole
 * record on each flush instead of tracking per-field dirty bits.
 *
 * Hydration / flush logic lives in `lib/user-prefs-sync.ts`; this
 * slice just owns the bookkeeping state.
 */

export interface UserPrefsSlice {
  /** True when local prefs have been edited since the last successful
   *  flush. Set by every action that mutates a synced pref; cleared by
   *  the sync layer after a successful PUT. */
  dirtyUserPrefs: boolean;
  /** ISO timestamp the server returned on the last successful hydrate.
   *  Used by the sync layer to skip no-op hydrations when the local
   *  cache is already current. */
  userPrefsHydratedAt: string | null;
  markUserPrefsDirty: () => void;
  clearUserPrefsDirty: () => void;
  setUserPrefsHydratedAt: (iso: string | null) => void;
}

export const createUserPrefsSlice: StateCreator<ItineraryState, [], [], UserPrefsSlice> = (set) => ({
  dirtyUserPrefs: false,
  userPrefsHydratedAt: null,
  markUserPrefsDirty: () => set({ dirtyUserPrefs: true }),
  clearUserPrefsDirty: () => set({ dirtyUserPrefs: false }),
  setUserPrefsHydratedAt: (iso) => set({ userPrefsHydratedAt: iso }),
});
